import { axios } from '@Api/axiosClient';
import type { VerificationCodeProviderType } from '@Enums/verificationCodeProviderType.enum';
import type { VerifyCodeResponse } from '@Types/VerifyCodeResponse.interface';
import type {
    SendCodeRequest,
    OtpSendResponse,
    OtpVerifyResponse,
    SelectOtpAccountRequest,
} from '@Types/otp.interface';
import type { CompleteLoginResponse } from '@Types/deviceAuth.interface';
import { CONFIG_FOR_STRING_POSTS } from './helpers.api';

export const sendPasscodeApi = async (providerType: VerificationCodeProviderType, phoneNumber: string) => {
    const payload = {
        "phoneNumber": phoneNumber,
        "providerType": providerType
    }

    const response = await axios.post('/api/AuthFlow/SendCode', payload);
    return response.data;
};

export const verifyPasscodeApi = async(code: string) : Promise<VerifyCodeResponse> => {
    const response = await axios.post(
        '/api/AuthFlow/VerifyCode',
        JSON.stringify(code),
        CONFIG_FOR_STRING_POSTS
    );
    return response?.data;
};

export const getRedactedClientPhoneNumber = async (): Promise<string> => {
    const response = await axios.get('/api/AuthFlow/GetRedactedClientPhoneNumber');
    return response?.data;
};

// ─── Basic-auth OTP endpoints ─────────────────────────────────────────────────
// These share the Auth BFF's /api/AuthFlow/SendCode, /VerifyCode,
// /VerifyEmailCode, /ResendEmailLoginCode, and /SelectOtpAccount endpoints.
// SendCode and ResendEmailLoginCode return the BffOtpStatus enum value as the
// raw response body, so we wrap it as { status } to match OtpSendResponse.

export const sendCodeApi = async (request: SendCodeRequest): Promise<OtpSendResponse> => {
    const payload = {
        phoneNumber: request.phoneNumber,
        providerType: request.deliveryType,
    };
    const response = await axios.post('/api/AuthFlow/SendCode', payload);
    return { status: response.data } as OtpSendResponse;
};

export const verifyCodeApi = async (request: { code: string }): Promise<OtpVerifyResponse> => {
    const response = await axios.post<OtpVerifyResponse>(
        '/api/AuthFlow/VerifyCode',
        JSON.stringify(request.code),
        CONFIG_FOR_STRING_POSTS
    );
    return response.data;
};

/**
 * Verifies an email OTP code. Body is a raw JSON string (the code itself),
 * mirroring the phone-OTP VerifyCode contract.
 */
export const verifyEmailCodeApi = async (request: { code: string }): Promise<OtpVerifyResponse> => {
    const response = await axios.post<OtpVerifyResponse>(
        '/api/AuthFlow/VerifyEmailCode',
        JSON.stringify(request.code),
        CONFIG_FOR_STRING_POSTS
    );
    return response.data;
};

/**
 * Resends the email login OTP code. No request body — the bff reuses the
 * session's existing email identifier. Returns the BffOtpStatus enum as the raw
 * response body (wrapped as { status }).
 */
export const resendEmailLoginCodeApi = async (): Promise<OtpSendResponse> => {
    const response = await axios.post('/api/AuthFlow/ResendEmailLoginCode');
    return { status: response.data } as OtpSendResponse;
};

/**
 * Sends an email OTP code as part of multi-channel selection.
 * Posts an empty JSON object: the bff's SendEmailCode action binds
 * [FromBody] SendEmailCodeRequest (all fields optional) and resolves the email
 * from the activity session. Posting with NO body omits the Content-Type header,
 * and the server rejects it with 415 Unsupported Media Type — so the `{}` is required.
 * Returns the BffOtpStatus enum as the raw response body (wrapped as { status }).
 */
export const sendEmailCodeApi = async (): Promise<OtpSendResponse> => {
    const response = await axios.post('/api/AuthFlow/SendEmailCode', {});
    return { status: response.data } as OtpSendResponse;
};

/**
 * Phone-OTP disambiguation: picks an (accountId, userId) pair after
 * VerifyCode returns DisambiguationRequired.
 */
export const selectOtpAccountApi = async (
    request: SelectOtpAccountRequest,
): Promise<CompleteLoginResponse> => {
    const response = await axios.post<CompleteLoginResponse>(
        '/api/AuthFlow/SelectOtpAccount',
        request,
    );
    return response.data;
};

/**
 * Phone-OTP "None of these are mine": signals that none of the disambiguated
 * accounts belong to the user. The BFF mints a fresh ClientId and proceeds
 * as a new client (token exchange + RDS routing).
 */
export const selectNoMatchedAccountApi = async (): Promise<CompleteLoginResponse> => {
    const response = await axios.post<CompleteLoginResponse>('/api/AuthFlow/SelectNoMatchedAccount');
    return response.data;
};
